/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import { CmdBuilder } from "@nuance/hap-components";
import { ControlType } from "@nuance/hierarchical-settings-ui-domain";

/**
 * Data passed back when help text changes
 */
export interface IHelpTextChangeData {
    /**
     * Tooltip text
     */
    tooltipText: string;
    /**
     * Helper text
     */
    helperText: string;
    /**
     * For the controls consisting of the HelpText
     * is everything correct.
     */
    isValid: boolean;
}

export interface IHelpTextProps {
    name?: string;
    tooltipText: string;
    helperText: string;
    onHelpTextChange?: (data: IHelpTextChangeData) => void;
    controlType: ControlType;
}

/**
 * Help text style
 */
export enum HelpStyle {
    /**
     * Help is tooltip
     */
    Tooltip,
    /**
     * Help is a secondary label
     */
    Label
}

/**
 * State for the HelpText component
 */
export interface IHelpTextState {
    includeHelpText: boolean;
    helpStyle: HelpStyle;
    helpText: string;
    helpTextErrorMsg: string;
    touched: boolean;
}

/**
 * Commands for the HelpText component
 */
export class HelpTextCommands {
    /**
     * Constructor
     */
    constructor() {
        const cb = new CmdBuilder();

        this.Cmds = {
            includeHelpTextChanged: cb.bindCmdNP(this.includeHelpTextChangedHandler),
            helpStyleChanged: cb.bindCmd(this.helpStyleChangedHandler),
            helpTextChanged: cb.bindCmd(this.helpTextChangedHandler),
            helpTextBlur: cb.bindCmdNP(this.helpTextBlurHandler)
            //postChanges: cb.bindCmdNP(this.postChangesHandler)
        };
        this.Reducer = cb.buildReducer<IHelpTextState>(this.Cmds);
    }

    /**
     * Commands
     */
    readonly Cmds;

    /**
     * Reducer function
     */
    readonly Reducer;

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    /**
     * Include HelpText checkbox changed
     *
     * @param state Old state
     * @returns New state
     */
    includeHelpTextChangedHandler = (state: IHelpTextState) => {
        state.includeHelpText = !state.includeHelpText;
        return state;
    };

    /**
     * Handles HelpStyle changed
     *
     * @param state Old state
     * @param key New HelpStyle option key
     * @returns New State
     */
    helpStyleChangedHandler = (state: IHelpTextState, key: string) => {
        state.helpStyle =
            key === HelpStyle[HelpStyle.Tooltip] ? HelpStyle.Tooltip : HelpStyle.Label;
        state.helpTextErrorMsg = this.getToolTipMessage(state);
        return state;
    };

    /**
     * Handles changes to the help text
     *
     * @param state Old State
     * @param tooltip New help text
     * @returns New state
     */
    helpTextChangedHandler = (state: IHelpTextState, helpText: string) => {
        state.helpText = helpText;
        return state;
    };

    /**
     * Handles validating help text and showing error when user
     * leave help text text box.
     *
     * @param state Old state
     * @returns New state
     */
    helpTextBlurHandler = (state: IHelpTextState) => {
        state.touched = true;
        state.helpTextErrorMsg = this.getToolTipMessage(state);
        return state;
    };

    /**
     * Calculates the items that have changed in order to
     * pass it up to the component's parent
     *
     * @param state Current state
     * @returns Change data
     */
    CalcChangeData = (state: IHelpTextState): IHelpTextChangeData => {
        const changeData: IHelpTextChangeData = {
            tooltipText: "",
            helperText: "",
            isValid: true
        };

        if (state.includeHelpText) {
            switch (state.helpStyle) {
                case HelpStyle.Tooltip:
                    changeData.tooltipText = state.helpText;
                    break;

                case HelpStyle.Label:
                    changeData.helperText = state.helpText;
                    break;
            }

            changeData.isValid = changeData.tooltipText !== "" || changeData.helperText !== "";
        }

        return changeData;
    };

    getToolTipMessage(state: IHelpTextState): string {
        let toolTipMessage = "";
        if (
            state.includeHelpText &&
            state.helpStyle === HelpStyle.Tooltip &&
            state.helpText === ""
        ) {
            toolTipMessage = "Please enter a tooltip.";
        } else if (
            state.includeHelpText &&
            state.helpStyle === HelpStyle.Label &&
            state.helpText === ""
        ) {
            toolTipMessage = "Please enter secondary text.";
        } else {
            toolTipMessage = "";
        }
        return toolTipMessage;
    }
}
